<script setup lang="ts">
import type { CommentItem } from '~/stores/comments'

const props = defineProps<{
  characterId: string
  disabled?: boolean
  primaryColor?: string
}>()

const emit = defineEmits<{
  'comment-posted': [comment: CommentItem]
}>()

const toast = useToast()
const content = ref('')
const isSubmitting = ref(false)

const charCount = computed(() => content.value.length)
const isValid = computed(() => content.value.trim().length >= 1 && content.value.length <= 100)

async function handleSubmit() {
  if (!isValid.value || isSubmitting.value) return

  isSubmitting.value = true
  try {
    const data = await $fetch<{ comment: CommentItem }>(`/api/character/${props.characterId}/comment`, {
      method: 'POST',
      body: { content: content.value.trim() }
    })
    content.value = ''
    emit('comment-posted', data.comment)
  } catch (err: any) {
    const message = err.data?.message || err.message || 'Failed to post comment'
    toast.add({
      title: 'Error',
      description: message,
      icon: 'i-lucide-alert-circle',
      color: 'error',
      duration: 5000
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <!-- Friends only: disabled state -->
  <div
    v-if="disabled"
    class="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/3 border border-white/6 opacity-50 cursor-not-allowed mb-5"
  >
    <UIcon
      name="i-lucide-lock"
      class="size-3 text-white/30 shrink-0"
    />
    <span class="text-xs text-white/30">Friends only</span>
  </div>

  <!-- Normal form -->
  <template v-else>
    <div class="flex gap-2 items-stretch">
      <div
        class="flex-1 relative"
        :style="primaryColor ? { '--ui-primary': primaryColor } : {}"
      >
        <UTextarea
          v-model="content"
          placeholder="Write a comment..."
          color="primary"
          autoresize
          :rows="1"
          :maxlength="100"
          class="w-full"
          :ui="{ base: 'bg-white/5 text-xs' }"
          @keydown.enter.meta.prevent="handleSubmit"
          @keydown.enter.ctrl.prevent="handleSubmit"
        />
        <span
          v-if="charCount > 0"
          class="absolute bottom-1.5 right-2 text-[10px] pointer-events-none"
          :class="charCount > 100 ? 'text-red-400' : 'text-white/30'"
        >
          {{ charCount }}/100
        </span>
      </div>
      <UButton
        icon="i-lucide-send"
        color="primary"
        size="xs"
        :loading="isSubmitting"
        :disabled="!isValid"
        class="self-stretch px-3"
        :style="primaryColor ? { '--ui-primary': primaryColor } : {}"
        @click="handleSubmit"
      />
    </div>
  </template>
</template>
